import React, {
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { connect } from 'react-redux';
import * as PropTypes from 'prop-types';
import { Redirect, Link, withRouter } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import '../registration/Registration.css';
import {
  Form, Icon, Input, Button, Row, Typography, Divider,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import ViaValidationAlert from '../../../components/fields/ViaValidationAlert';
import LocaleSelector from '../../../components/translations/LocaleSelector';
import routeLocations from '../../../utility/route-locations';
import { selectSessionUser } from '../../../state/ui/application/sessionUiReducer';
import { selectSendResetPasswordEmailRequest } from '../../../state/ui/configuration/users/passwordUiReducer';
import { requestResetPasswordEmail } from '../../../state/ui/configuration/users/passwordUiActions';
import { selectCurrentLoginAddress } from '../../../state/ui/login/loginUiReducer';
import { setCurrentLoginAddress } from '../../../state/ui/login/loginUiActions';
import { ViaLogo } from '../../../components/logo/ViaLogo';

const { Text } = Typography;


const createUsernameField = (form, intl, defaultAddress) => form.getFieldDecorator('username', {
  initialValue: defaultAddress,
  rules: [
    { required: true, message: intl.formatMessage({ id: 'validationEmail' }) },
    { type: 'email', message: intl.formatMessage({ id: 'invalidEmail' }) },
  ],
})(
  <Input
    prefix={<Icon type="user" style={{ color: 'rgba(0,0,0,0.25)' }} />}
    type="email"
    placeholder={intl.formatMessage({ id: 'email' })}
    maxLength={80}
    style={{ marginBottom: '0px' }}
  />,
);

// The root registration component.
// Checks if the user is done with authentication and redirects them.
// Otherwise displays the registration form.
function ForgotPassword({
  form, sessionUser, loading, sendError, resetPassword, defaultAddress, setLoginAddress, history,
}) {
  const intl = useIntl();
  const recaptchaRef = useRef();

  const handleSubmit = useCallback((token) => {
    // During the Recaptcha, a user can still modify the fields, so we need to re-validate.
    form.validateFields((err, values) => {
      if (!err && !loading) {
        recaptchaRef.current.reset();
        resetPassword(values.username, token, history);
      }
    });
  }, [form, resetPassword, history, loading]);

  useEffect(() => { setLoginAddress(form.getFieldValue('username')); }, [form, setLoginAddress]);

  const preSubmit = (useCallback((e) => {
    e.preventDefault();
    form.validateFields((err) => {
      if (!err) {
        recaptchaRef.current.execute();
      }
    });
  }, [form, recaptchaRef]));

  if (sessionUser) {
    return <Redirect to={{ pathname: routeLocations.dashboard }} />;
  }


  return (
    <div className="app-registration-layout">
      <Row type="flex" align="middle" justify="center">
        <div className="app-registration-content">
          <Row type="flex" justify="center">
            <ViaLogo href={routeLocations.landing} className="app-registration-logo" />
          </Row>
          <Row type="flex" justify="center">
            <Text className="registration-text"><FormattedMessage id="emailPlease" /></Text>
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.REACT_APP_RECAPTCHA}
              onChange={handleSubmit}
            />
          </Row>
          <Row type="flex" justify="center">
            <ViaValidationAlert validationError={sendError} closable className="registration-checkbox-alert" />
          </Row>
          <Row>
            <Form onSubmit={e => preSubmit(e)} className="app-login-form">
              <Form.Item style={{ marginBottom: '0px' }}>
                { createUsernameField(form, intl, defaultAddress) }
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  block
                  htmlType="submit"
                  loading={loading}
                  className="app-registration-button"
                >
                  <FormattedMessage id="sendEmail" />
                </Button>
              </Form.Item>
            </Form>
          </Row>
          <Row type="flex" justify="center">
            <LocaleSelector />
          </Row>
          <Divider />
          <Row type="flex" justify="center">
            <Link className="login-form-sign-up" to={routeLocations.login}><FormattedMessage id="returnToLogin" /></Link>
          </Row>
        </div>
      </Row>
    </div>
  );
}


ForgotPassword.propTypes = {
  history:
    PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  location:
    PropTypes.shape({
      state: PropTypes.shape({
        sourceLocation: PropTypes.shape({
          pathname: PropTypes.string.isRequired,
        }),
      }),
    }).isRequired,
  sessionUser: PropTypes.shape({ username: PropTypes.string.isRequired }),
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  sendError: PropTypes.shape({
    errorMessage: PropTypes.string.isRequired,
    errors: PropTypes.array.isRequired,
  }),
  resetPassword: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  defaultAddress: PropTypes.string,
  setLoginAddress: PropTypes.func.isRequired,
};

ForgotPassword.defaultProps = {
  sessionUser: null,
  sendError: null,
  loading: false,
  defaultAddress: null,
};

const mapStateToProps = (state) => {
  const resetPasswordEmailRequest = selectSendResetPasswordEmailRequest(state);

  return {
    sessionUser: selectSessionUser(state),
    loading: resetPasswordEmailRequest ? resetPasswordEmailRequest.isLoading : false,
    sendError: resetPasswordEmailRequest ? resetPasswordEmailRequest.error : null,
    defaultAddress: selectCurrentLoginAddress(state),
  };
};

const mapDispatchToProps = dispatch => ({
  resetPassword: (username, reCaptcha, history) => dispatch(requestResetPasswordEmail(username, reCaptcha, history)),
  setLoginAddress: email => dispatch(setCurrentLoginAddress(email)),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withRouter(
    Form.create({ name: 'Forgot_Password' })(ForgotPassword),
  ),
);
